import { useCallback, useEffect, useMemo, useState } from "react";
import Sidebar from "../components/Sidebar";
import ESMPTable from "../components/ESMPTable";
import api from "../services/api";

export default function AdminApprovedESMPs() {
  const [search, setSearch] = useState("");
  const [esmps, setEsmps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEsmp, setSelectedEsmp] = useState(null);
  const [decision, setDecision] = useState("");
  const [decisionFile, setDecisionFile] = useState(null);
  const [comments, setComments] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const fetchApproved = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/esmp/status/Approved");
      const data = Array.isArray(res.data?.data) ? res.data.data : [];
      const normalized = data.map((item) => ({
        ...item,
        projectName: item.project_name || item.projectName || "",
        submittedBy: item.User?.name || item.submittedBy || "N/A",
      }));
      setEsmps(normalized);
    } catch (err) {
      console.error("Error fetching approved ESMPs:", err);
      setEsmps([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchApproved();
  }, [fetchApproved]);

  const filtered = useMemo(
    () =>
      esmps.filter(
        (e) =>
          (e.projectName || "").toLowerCase().includes(search.toLowerCase()) ||
          (e.district || "").toLowerCase().includes(search.toLowerCase()) ||
          (e.submittedBy || "").toLowerCase().includes(search.toLowerCase())
      ),
    [esmps, search]
  );

  const openDecisionModal = (esmp) => {
    setSelectedEsmp(esmp);
    setDecision("");
    setDecisionFile(null);
    setComments("");
    setUploadProgress(0);
  };

  const closeDecisionModal = () => {
    if (submitting) return;
    setSelectedEsmp(null);
    setDecision("");
    setDecisionFile(null);
    setComments("");
    setUploadProgress(0);
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 KB";
    const units = ["B", "KB", "MB", "GB"];
    const index = Math.min(
      Math.floor(Math.log(bytes) / Math.log(1024)),
      units.length - 1
    );
    return `${(bytes / 1024 ** index).toFixed(index === 0 ? 0 : 1)} ${units[index]}`;
  };

  const submitDecision = async () => {
    if (!decision) {
      window.alert("Please select an Admin decision.");
      return;
    }
    if (!decisionFile) {
      window.alert("Please attach the Admin decision document.");
      return;
    }

    const decisionName =
      decision === "clear" ? "Clear for Submission" : "Return to Reviewer";
    const confirmed = window.confirm(
      `${decisionName} for ${selectedEsmp.esmp_id}? The attached document will become the current version under the same ESMP ID.`
    );
    if (!confirmed) return;

    const payload = new FormData();
    payload.append("decision", decision);
    payload.append("comments", comments);
    payload.append("decision_file", decisionFile);

    try {
      setSubmitting(true);
      setUploadProgress(0);
      const response = await api.put(
        `/esmp/${selectedEsmp.id}/admin-decision`,
        payload,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (event) => {
            if (event.total) {
              setUploadProgress(
                Math.round((event.loaded * 100) / event.total)
              );
            }
          },
        }
      );

      window.alert(
        response.data?.message || "Admin decision saved successfully."
      );
      setSelectedEsmp(null);
      setDecision("");
      setDecisionFile(null);
      setComments("");
      setUploadProgress(0);
      await fetchApproved();
    } catch (error) {
      console.error("Failed to save Admin decision:", error);
      window.alert(
        error.response?.data?.message ||
          "The Admin decision could not be saved. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-gray-100">
      <Sidebar />
      <main className="flex-1 p-6 space-y-6">
        <div className="flex justify-between items-center bg-gradient-to-r from-green-700 to-green-500 text-white p-5 rounded-xl shadow-md">
          <h2 className="text-2xl font-bold">Admin - Approved ESMPs</h2>
          <input
            type="text"
            placeholder="Search ESMP..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="px-3 py-2 rounded text-black border focus:ring-2 focus:ring-green-600 outline-none"
          />
        </div>

        <div className="bg-white p-6 rounded-xl shadow">
          {loading ? (
            <p className="text-gray-500">Loading approved ESMPs...</p>
          ) : (
            <ESMPTable
              data={filtered}
              emptyMessage="No approved ESMPs."
              showDownloadButton
              showAdminDecisionButton
              onAdminDecision={openDecisionModal}
            />
          )}
        </div>
      </main>

      {selectedEsmp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="max-h-[92vh] w-full max-w-xl overflow-y-auto rounded-xl bg-white p-6 shadow-xl">
            <div className="mb-5 flex items-start justify-between">
              <div>
                <h3 className="text-xl font-bold text-gray-800">
                  Admin Final Decision
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  ESMP ID and project information will remain unchanged.
                </p>
              </div>
              <button
                type="button"
                onClick={closeDecisionModal}
                disabled={submitting}
                className="text-2xl leading-none text-gray-500 hover:text-gray-800"
                aria-label="Close"
              >
                &times;
              </button>
            </div>

            <div className="mb-5 rounded-lg bg-gray-50 p-4 text-sm">
              <span className="block text-gray-500">ESMP ID</span>
              <strong>{selectedEsmp.esmp_id}</strong>
              <span className="mt-3 block text-gray-500">Project</span>
              <strong>{selectedEsmp.projectName}</strong>
              <span className="mt-3 block text-gray-500">District</span>
              <strong>{selectedEsmp.district}</strong>
            </div>

            <fieldset disabled={submitting}>
              <legend className="mb-2 text-sm font-semibold text-gray-700">
                Select decision
              </legend>
              <div className="grid gap-3 sm:grid-cols-2">
                <label
                  className={`cursor-pointer rounded-lg border p-4 ${
                    decision === "clear"
                      ? "border-green-600 bg-green-50"
                      : "border-gray-300"
                  }`}
                >
                  <input
                    type="radio"
                    name="adminDecision"
                    value="clear"
                    checked={decision === "clear"}
                    onChange={(event) => setDecision(event.target.value)}
                    className="mr-2"
                  />
                  <strong className="text-green-700">
                    Clear for Submission
                  </strong>
                  <span className="mt-1 block text-xs text-gray-600">
                    Final clearance. This completes and locks the workflow.
                  </span>
                </label>

                <label
                  className={`cursor-pointer rounded-lg border p-4 ${
                    decision === "return"
                      ? "border-orange-500 bg-orange-50"
                      : "border-gray-300"
                  }`}
                >
                  <input
                    type="radio"
                    name="adminDecision"
                    value="return"
                    checked={decision === "return"}
                    onChange={(event) => setDecision(event.target.value)}
                    className="mr-2"
                  />
                  <strong className="text-orange-700">
                    Return to Reviewer
                  </strong>
                  <span className="mt-1 block text-xs text-gray-600">
                    Sends the same ESMP back to its assigned reviewer.
                  </span>
                </label>
              </div>
            </fieldset>

            <label className="mb-2 mt-5 block text-sm font-semibold text-gray-700">
              Attach decision document
            </label>
            <input
              type="file"
              name="decision_file"
              onChange={(event) =>
                setDecisionFile(event.currentTarget.files?.[0] || null)
              }
              disabled={submitting}
              className="w-full rounded-lg border border-gray-300 p-3"
            />

            {decisionFile && (
              <div className="mt-3 rounded-lg border border-green-200 bg-green-50 p-3 text-sm">
                <strong className="block break-all text-green-800">
                  {decisionFile.name}
                </strong>
                <span className="text-green-700">
                  {formatFileSize(decisionFile.size)}
                </span>
              </div>
            )}

            <label className="mb-2 mt-5 block text-sm font-semibold text-gray-700">
              Admin comments (optional)
            </label>
            <textarea
              rows={4}
              value={comments}
              onChange={(event) => setComments(event.target.value)}
              disabled={submitting}
              placeholder="Add instructions, clarification, or final notes..."
              className="w-full rounded-lg border border-gray-300 p-3 outline-none focus:ring-2 focus:ring-purple-500"
            />

            {submitting && (
              <div className="mt-5">
                <div className="mb-1 flex justify-between text-sm text-gray-600">
                  <span>Uploading Admin decision...</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="h-3 overflow-hidden rounded-full bg-gray-200">
                  <div
                    className="h-full bg-purple-600 transition-all"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}

            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeDecisionModal}
                disabled={submitting}
                className="rounded-lg border border-gray-300 px-4 py-2 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitDecision}
                disabled={submitting}
                className="rounded-lg bg-purple-600 px-4 py-2 text-white hover:bg-purple-700 disabled:opacity-50"
              >
                {submitting ? "Saving Decision..." : "Confirm Decision"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
